import { useState, useEffect, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  ClockIcon,
  TagIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { WARNING_EMOJI } from '../utils/genderDisplay';
import {
  getChicks,
  getUnbandedChicks,
  getBandedChicks,
  createChick,
  updateChick,
  deleteChick,
  bandChick,
  getChickStatistics,
  type ChickInfo,
  type CreateChickDto,
  type UpdateChickDto,
  type BandChickDto,
  type ChickStatistics
} from '../api/chicks';
import { ChickRearMethod, Gender, GenderDeterminationMethod } from '../utils/enums';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from '../components/ui/BrandModal';

// Import the modal components
import CreateChickForm from '../components/chicks/CreateChickForm';
import EditChickForm from '../components/chicks/EditChickForm';
import ChickDetailsView from '../components/chicks/ChickDetailsView';
import BandChickForm from '../components/chicks/BandChickForm';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

interface ChickFilters {
  rearMethod?: ChickRearMethod;
  isBanded?: boolean;
  speciesId?: string;
  ageRange?: { min: number; max: number };
}

export default function Chicks() {
  // Data state
  const [chicks, setChicks] = useState<ChickInfo[]>([]);
  const [statistics, setStatistics] = useState<ChickStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ChickFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof ChickInfo>('hatchDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBandModal, setShowBandModal] = useState(false);
  const [selectedChick, setSelectedChick] = useState<ChickInfo | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [chicksData, statsData] = await Promise.all([
        getChicks(),
        getChickStatistics()
      ]);
      setChicks(Array.isArray(chicksData) ? chicksData : []);
      setStatistics(statsData);
      setError(null);
    } catch (err) {
      setError('Failed to load chicks data');
      console.error('Error loading chicks:', err);
      toast.error('Failed to load chicks data');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredChicks = useMemo(() => {
    let result = chicks;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((chick) =>
        chick.species?.commonName?.toLowerCase().includes(term) ||
        chick.species?.genus?.toLowerCase().includes(term) ||
        chick.maleParent?.bandId?.toLowerCase().includes(term) ||
        chick.femaleParent?.bandId?.toLowerCase().includes(term) ||
        chick.cageNumber?.toLowerCase().includes(term) ||
        chick.bandNumber?.toLowerCase().includes(term) ||
        chick.notes?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.rearMethod) {
      result = result.filter((chick) => chick.rearMethod === filters.rearMethod);
    }
    if (filters.isBanded !== undefined) {
      result = result.filter((chick) => chick.isBanded === filters.isBanded);
    }
    if (filters.speciesId) {
      result = result.filter((chick) => chick.species?.id === filters.speciesId);
    }
    if (filters.ageRange) {
      result = result.filter(
        (chick) =>
          chick.ageInDays >= filters.ageRange!.min &&
          chick.ageInDays <= filters.ageRange!.max
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      if (bVal === undefined) return sortOrder === 'asc' ? 1 : -1;

      const aStr = String(aVal);
      const bStr = String(bVal);

      if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [chicks, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredChicks.length / itemsPerPage);
  const paginatedChicks = filteredChicks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const uniqueSpecies = [...new Set(chicks.map((chick) => chick.species?.id).filter(Boolean))];
  const speciesMap = new Map(chicks.filter(c => c.species).map(c => [c.species!.id, c.species!]));

  // Event handlers
  const handleCreateChick = async (chickData: CreateChickDto) => {
    try {
      const newChick = await createChick(chickData);
      setChicks(prev => [...prev, newChick]);
      setShowCreateModal(false);
      toast.success('Chick record created successfully');
      loadData(); // Refresh statistics
    } catch (err) {
      console.error('Error creating chick:', err);
      toast.error('Failed to create chick record');
    }
  };

  const handleEditChick = async (id: string, chickData: UpdateChickDto) => {
    try {
      const updatedChick = await updateChick(id, chickData);
      setChicks(prev => prev.map(chick => chick.id === id ? updatedChick : chick));
      setShowEditModal(false);
      setSelectedChick(null);
      toast.success('Chick updated successfully');
      loadData(); // Refresh statistics
    } catch (err) {
      console.error('Error updating chick:', err);
      toast.error('Failed to update chick');
    }
  };

  const handleDeleteChick = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this chick record? This will revert the egg to unlaid status.')) {
      return;
    }
    
    try {
      await deleteChick(id);
      setChicks(prev => prev.filter(chick => chick.id !== id));
      toast.success('Chick record removed successfully');
      loadData(); // Refresh statistics
    } catch (err) {
      console.error('Error deleting chick:', err);
      toast.error('Failed to remove chick record');
    }
  };

  const handleBandChick = async (id: string, bandData: BandChickDto) => {
    try {
      const newBird = await bandChick(id, bandData);
      setChicks(prev => prev.map(chick => 
        chick.id === id 
          ? { ...chick, isBanded: true, bandingDate: new Date(bandData.bandingDate), bandNumber: bandData.bandId }
          : chick
      ));
      setShowBandModal(false);
      setSelectedChick(null);
      toast.success(`Chick banded successfully! Bird record created with ID: ${newBird.bandId}`);
      const comparison = newBird?.geneticsComparison;
      if (comparison?.message) {
        if (comparison.matched) {
          toast.success(comparison.message, { duration: 5000 });
        } else {
          toast(comparison.message, { icon: WARNING_EMOJI, duration: 6000 });
        }
      }
      loadData(); // Refresh statistics
    } catch (err) {
      console.error('Error banding chick:', err);
      toast.error('Failed to band chick');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
  };

  const openDetailsModal = (chick: ChickInfo) => {
    setSelectedChick(chick);
    setShowDetailsModal(true);
  };

  const openEditModal = (chick: ChickInfo) => {
    setSelectedChick(chick);
    setShowEditModal(true);
  };

  const openBandModal = (chick: ChickInfo) => {
    setSelectedChick(chick);
    setShowBandModal(true);
  };

  const getAgeColor = (ageInDays: number) => {
    if (ageInDays < 7) return 'text-green-600';
    if (ageInDays < 21) return 'text-yellow-600';
    if (ageInDays < 42) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAgeLabel = (ageInDays: number) => {
    if (ageInDays === 0) return 'Today';
    if (ageInDays === 1) return '1 day old';
    if (ageInDays < 7) return `${ageInDays} days old`;
    if (ageInDays < 14) return `${Math.floor(ageInDays / 7)} week old`;
    if (ageInDays < 21) return `${Math.floor(ageInDays / 7)} weeks old`;
    return `${ageInDays} days old`;
  };

  const getRearMethodBadgeColor = (method: ChickRearMethod) => {
    switch (method) {
      case ChickRearMethod.PARENT_REARED:
        return 'bg-emerald-100 text-emerald-800';
      case ChickRearMethod.HAND_REARED:
        return 'bg-cyan-100 text-cyan-800';
      case ChickRearMethod.FOSTER_REARED:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <PageHero
        title="Chicks"
        subtitle="Manage hatched and fledged chicks until banding"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Chick
          </button>
        }
      />

      {/* Statistics Cards */}
      {statistics && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <HeartIcon className="h-6 w-6 text-slate-400" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Chicks</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.totalChicks}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Unbanded</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.unbandedChicks}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TagIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Banded</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.bandedChicks}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Average Age</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.averageAgeInDays} days</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-lg">
          <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-600">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              placeholder="Search chicks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ${
              showFilters
                ? 'bg-cyan-50 text-cyan-700 ring-cyan-600'
                : 'bg-white text-slate-900 ring-gray-300 hover:bg-slate-50/80'
            }`}
          >
            <FunnelIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Filters
          </button>

          <div className="flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 ${
                viewMode === 'grid'
                  ? 'bg-cyan-50 text-cyan-700 ring-cyan-600'
                  : 'bg-white text-slate-900 hover:bg-slate-50/80'
              }`}
            >
              <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 ${
                viewMode === 'table'
                  ? 'bg-cyan-50 text-cyan-700 ring-cyan-600'
                  : 'bg-white text-slate-900 hover:bg-slate-50/80'
              }`}
            >
              <TableCellsIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-4 bg-slate-50/80 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Rear Method</label>
              <select
                value={filters.rearMethod || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, rearMethod: e.target.value as ChickRearMethod || undefined }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option key="empty-option-1753461297361" value="">All Methods</option>
                {Object.values(ChickRearMethod).map(method => (
                  <option key={method} value={method}>
                    {method.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Banding Status</label>
              <select
                value={filters.isBanded === undefined ? '' : filters.isBanded ? 'true' : 'false'}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  isBanded: e.target.value === '' ? undefined : e.target.value === 'true' 
                }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option key="empty-option-1753461297362" value="">All Chicks</option>
                <option key="false-1753461297362" value="false">Unbanded</option>
                <option key="true-1753461297362" value="true">Banded</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Species</label>
              <select
                value={filters.speciesId || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, speciesId: e.target.value || undefined }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option key="empty-option-1753461297362" value="">All Species</option>
                {uniqueSpecies.map(speciesId => {
                  const species = speciesMap.get(speciesId);
                  return (
                    <option key={speciesId} value={speciesId}>
                      {species?.commonName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearFilters}
                className="btn btn-secondary text-sm"
              >
                <XMarkIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredChicks.length === 0 ? (
        <EmptyState
          title="No chicks found"
          description={
            searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your search or filters.'
              : 'Hatched and fledged chicks appear here until they are banded.'
          }
          icon={<HeartIcon className="h-6 w-6" />}
          action={
            !searchTerm && Object.keys(filters).length === 0 ? (
              <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
                <PlusIcon className="h-5 w-5" />
                Add Chick
              </button>
            ) : undefined
          }
        />
      ) : viewMode === 'grid' ? (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedChicks.map((chick) => (
            <div key={chick.id} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <HeartIcon className="h-8 w-8 text-pink-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-900">
                        {chick.species?.commonName || 'Unknown Species'}
                      </p>
                      <p className="text-xs text-slate-500">
                        Cage {chick.cageNumber}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!chick.isBanded && (
                      <button
                        onClick={() => openBandModal(chick)}
                        className="inline-flex items-center rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-green-500"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        Band
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Age:</span>
                    <span className={`font-medium ${getAgeColor(chick.ageInDays)}`}>
                      {getAgeLabel(chick.ageInDays)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-500">Hatched:</span>
                    <span className="text-slate-900">
                      {chick.hatchDate.toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-500">Method:</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getRearMethodBadgeColor(chick.rearMethod)}`}>
                      {chick.rearMethod.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-500">Status:</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      chick.isBanded
                        ? 'bg-emerald-100 text-emerald-800'
                        : chick.status === 'FLEDGED'
                          ? 'bg-teal-100 text-teal-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}>
                      {chick.isBanded ? 'Banded' : chick.status === 'FLEDGED' ? 'Fledged' : 'Unbanded'}
                    </span>
                  </div>

                  {chick.isBanded && chick.bandNumber && (
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-slate-500">Band #:</span>
                      <span className="text-slate-900 font-mono">
                        {chick.bandNumber}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-between">
                  <button
                    onClick={() => openDetailsModal(chick)}
                    className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(chick)}
                      className="inline-flex items-center rounded-md bg-cyan-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteChick(chick.id)}
                      className="inline-flex items-center rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Table view implementation here
        <div className="mt-6">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Species
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Age
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Hatch Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Rear Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Cage
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedChicks.map((chick) => (
                  <tr key={chick.id} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <HeartIcon className="h-5 w-5 text-pink-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {chick.species?.commonName || 'Unknown'}
                          </div>
                          <div className="text-sm text-slate-500">
                            {chick.species?.genus} {chick.species?.species}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getAgeColor(chick.ageInDays)}`}>
                        {getAgeLabel(chick.ageInDays)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {chick.hatchDate.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getRearMethodBadgeColor(chick.rearMethod)}`}>
                        {chick.rearMethod.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        chick.isBanded
                          ? 'bg-emerald-100 text-emerald-800'
                          : chick.status === 'FLEDGED'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-amber-100 text-amber-800'
                      }`}>
                        {chick.isBanded
                          ? `Banded (${chick.bandNumber})`
                          : chick.status === 'FLEDGED'
                            ? 'Fledged'
                            : 'Unbanded'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {chick.cageNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {!chick.isBanded && (
                          <button
                            onClick={() => openBandModal(chick)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <TagIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openDetailsModal(chick)}
                          className="text-cyan-600 hover:text-blue-900"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(chick)}
                          className="text-cyan-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteChick(chick.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredChicks.length)}
                </span>{' '}
                of <span className="font-medium">{filteredChicks.length}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      page === currentPage
                        ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                        : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create Chick Modal */}
      <BrandModal open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidthClass="max-w-lg">
        <BrandModalHeader title="Create Chick Record" onClose={() => setShowCreateModal(false)} />
        <BrandModalBody>
          <CreateChickForm
            onSubmit={handleCreateChick}
            onCancel={() => setShowCreateModal(false)}
          />
        </BrandModalBody>
      </BrandModal>

      {/* Edit Chick Modal */}
      <BrandModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedChick(null);
        }}
        maxWidthClass="max-w-lg"
      >
        <BrandModalHeader
          title="Edit Chick Record"
          onClose={() => {
            setShowEditModal(false);
            setSelectedChick(null);
          }}
        />
        <BrandModalBody>
          {selectedChick && (
            <EditChickForm
              chick={selectedChick}
              onSubmit={(data) => handleEditChick(selectedChick.id, data)}
              onCancel={() => {
                setShowEditModal(false);
                setSelectedChick(null);
              }}
            />
          )}
        </BrandModalBody>
      </BrandModal>

      {/* View Details Modal */}
      <BrandModal
        open={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedChick(null);
        }}
        maxWidthClass="max-w-2xl"
      >
        <BrandModalHeader
          title="Chick Details"
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedChick(null);
          }}
        />
        <BrandModalBody>
          {selectedChick && (
            <ChickDetailsView
              chick={selectedChick}
              onClose={() => {
                setShowDetailsModal(false);
                setSelectedChick(null);
              }}
            />
          )}
        </BrandModalBody>
      </BrandModal>

      {/* Band Chick Modal */}
      <BrandModal
        open={showBandModal}
        onClose={() => {
          setShowBandModal(false);
          setSelectedChick(null);
        }}
        maxWidthClass="max-w-lg"
      >
        <BrandModalHeader
          title="Band Chick"
          onClose={() => {
            setShowBandModal(false);
            setSelectedChick(null);
          }}
        />
        <BrandModalBody>
          {selectedChick && (
            <BandChickForm
              chick={selectedChick}
              onSubmit={(data) => handleBandChick(selectedChick.id, data)}
              onCancel={() => {
                setShowBandModal(false);
                setSelectedChick(null);
              }}
            />
          )}
        </BrandModalBody>
      </BrandModal>
    </div>
  );
} 